import { proxy } from 'valtio';
import { devtools } from 'valtio/utils';

import earlyTrialCompletion from '@cartwhl/common/api/earlyTrialCompletion';
import { getStripeProduct } from '@cartwhl/common/api/getStripeProduct';
import * as noticesStore from '@cartwhl/common/stores/noticesStore';
import * as userStore from '@cartwhl/common/stores/userStore';
import { OrganizationInfo, StripePrice, StripeProduct } from '@cartwhl/common/types';
import { getStripeProductID } from '@cartwhl/common/utils/getStripeProductID';
import handleGenericErrors from '@cartwhl/common/utils/handleGenericErrors';
import {
  getOrgAvailableCredits,
  isOrgCreditsBelowTenPercentOfMonthly,
  isOrgCreditsWorkspace,
} from '@cartwhl/common/utils/organizationWorkspace';

export type UpdatedProductKey =
  | 'individual_basic'
  | 'individual_pro'
  | 'team_standard'
  | 'team_pro'
  | 'team_scale';

export const UPDATED_PRODUCT_KEYS: UpdatedProductKey[] = [
  'individual_basic',
  'individual_pro',
  'team_standard',
  'team_pro',
  'team_scale',
];

export const INDIVIDUAL_PRODUCT_KEYS: UpdatedProductKey[] = [
  'individual_basic',
  'individual_pro',
];

export const TEAM_PRODUCT_KEYS: UpdatedProductKey[] = [
  'team_standard',
  'team_pro',
  'team_scale',
];

export type UpdatedProductStoreState = {
  products: Record<UpdatedProductKey, StripeProduct | null>;
  prices: StripePrice[];
  loading: boolean;
  done: boolean;
};

export type PurchaseCreditsModalVariant = 'low_credit' | 'out_of_credits';

export type PurchaseCreditsModalState = {
  isOpen: boolean;
  variant: PurchaseCreditsModalVariant;
  creditsRemaining: number;
};

/** Global org credit purchase modal; use with `PurchaseCreditsModalHost` in the app shell. */
export const purchaseCreditsModalState = proxy<PurchaseCreditsModalState>({
  isOpen: false,
  variant: 'low_credit',
  creditsRemaining: 0,
});

export function openPurchaseCreditsModal(params: {
  variant: PurchaseCreditsModalVariant;
  creditsRemaining: number;
}): void {
  purchaseCreditsModalState.variant = params.variant;
  purchaseCreditsModalState.creditsRemaining = params.creditsRemaining;
  purchaseCreditsModalState.isOpen = true;
}

export function closePurchaseCreditsModal(): void {
  purchaseCreditsModalState.isOpen = false;
}

/** Global "activate plan early" (end Stripe trial) modal; use with `ActivateEarlyTrialModalHost`. */
export const activateEarlyTrialModalState = proxy<{ isOpen: boolean }>({
  isOpen: false,
});

export function openActivateEarlyTrialModal(): void {
  activateEarlyTrialModalState.isOpen = true;
}

export function closeActivateEarlyTrialModal(): void {
  activateEarlyTrialModalState.isOpen = false;
}

const ORG_CREDITS_MODAL_SNOOZE_MS = 2 * 60 * 60 * 1000;
export const ORG_TRIAL_ORG_CREDIT_STARTER_AMOUNT = 120;

function orgCreditsModalSnoozeStorageKey(organizationID: string): string {
  return `cartwheel.orgCreditsPurchaseModal.snoozeUntil.${organizationID}`;
}

export function isPurchaseCreditsModalSnoozed(organizationID: string): boolean {
  if (typeof localStorage === 'undefined') {
    return false;
  }
  try {
    const raw = localStorage.getItem(orgCreditsModalSnoozeStorageKey(organizationID));
    if (!raw) {
      return false;
    }
    const until = parseInt(raw, 10);
    if (Number.isNaN(until) || until <= Date.now()) {
      localStorage.removeItem(orgCreditsModalSnoozeStorageKey(organizationID));
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** Persist snooze for this org and close the modal. */
export function snoozePurchaseCreditsModalForTwoHours(organizationID: string): void {
  if (typeof localStorage !== 'undefined') {
    try {
      const until = Date.now() + ORG_CREDITS_MODAL_SNOOZE_MS;
      localStorage.setItem(
        orgCreditsModalSnoozeStorageKey(organizationID),
        String(until),
      );
    } catch {
      /* quota / private mode */
    }
  }
  closePurchaseCreditsModal();
}

/**
 * After loading org details: open the credits modal if balance is under 10% of monthly included,
 * unless snoozed or already open.
 */
export function maybeOpenPurchaseCreditsModalFromOrgInfo(
  organizationInfo: OrganizationInfo | undefined | null,
): void {
  if (organizationInfo?.inStripeFreeTrialPeriod === true) {
    return;
  }
  const organizationID = organizationInfo?.organizationID?.trim();
  if (!organizationID || !isOrgCreditsBelowTenPercentOfMonthly(organizationInfo)) {
    return;
  }
  if (isPurchaseCreditsModalSnoozed(organizationID)) {
    return;
  }
  if (purchaseCreditsModalState.isOpen) {
    return;
  }
  if (activateEarlyTrialModalState.isOpen) {
    return;
  }
  const available = getOrgAvailableCredits(organizationInfo);
  openPurchaseCreditsModal({
    variant: available <= 0 ? 'out_of_credits' : 'low_credit',
    creditsRemaining: available,
  });
}

/**
 * When trialing, available credits is strictly under this, we may auto-open
 * {@link openActivateEarlyTrialModal} (same 2h snooze pattern as purchase credits).
 */
export const ACTIVATE_EARLY_TRIAL_AUTOLOAD_MAX_CREDITS = 30;

function activateEarlyTrialAutoloadSnoozeStorageKey(organizationID: string): string {
  return `cartwheel.orgActivateEarlyTrialAutoloadModal.snoozeUntil.${organizationID}`;
}

export function isActivateEarlyTrialAutoloadSnoozed(
  organizationID: string,
): boolean {
  if (typeof localStorage === 'undefined') {
    return false;
  }
  try {
    const raw = localStorage.getItem(
      activateEarlyTrialAutoloadSnoozeStorageKey(organizationID),
    );
    if (!raw) {
      return false;
    }
    const until = parseInt(raw, 10);
    if (Number.isNaN(until) || until <= Date.now()) {
      localStorage.removeItem(
        activateEarlyTrialAutoloadSnoozeStorageKey(organizationID),
      );
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function snoozeActivateEarlyTrialAutoloadForTwoHours(
  organizationID: string,
): void {
  if (typeof localStorage !== 'undefined') {
    try {
      const until = Date.now() + ORG_CREDITS_MODAL_SNOOZE_MS;
      localStorage.setItem(
        activateEarlyTrialAutoloadSnoozeStorageKey(organizationID),
        String(until),
      );
    } catch {
      /* quota / private mode */
    }
  }
  closeActivateEarlyTrialModal();
}

/**
 * After loading org (same entry point as {@link maybeOpenPurchaseCreditsModalFromOrgInfo}):
 * if the workspace is in a Stripe trial and has fewer than
 * {@link ACTIVATE_EARLY_TRIAL_AUTOLOAD_MAX_CREDITS} credits, open the activate-early
 * modal unless snoozed or another global modal is already open.
 */
export function maybeOpenActivateEarlyTrialFromOrgInfo(
  organizationInfo: OrganizationInfo | undefined | null,
): void {
  if (organizationInfo?.inStripeFreeTrialPeriod !== true) {
    return;
  }
  const organizationID = organizationInfo.organizationID?.trim();
  if (!organizationID || !isOrgCreditsWorkspace(organizationInfo)) {
    return;
  }
  const available = getOrgAvailableCredits(organizationInfo);
  if (available >= ACTIVATE_EARLY_TRIAL_AUTOLOAD_MAX_CREDITS) {
    return;
  }
  if (isActivateEarlyTrialAutoloadSnoozed(organizationID)) {
    return;
  }
  if (activateEarlyTrialModalState.isOpen) {
    return;
  }
  if (purchaseCreditsModalState.isOpen) {
    return;
  }
  openActivateEarlyTrialModal();
}

export const state = proxy<UpdatedProductStoreState>({
  loading: false,
  products: {
    individual_basic: null,
    individual_pro: null,
    team_standard: null,
    team_pro: null,
    team_scale: null,
  },
  prices: [],
  done: false,
});

if (process.env.NODE_ENV === 'development') {
  devtools(state, { name: 'UpdatedProduct', enabled: true });
}

export async function fetchProducts() {
  if (state.loading || state.done) return;

  state.loading = true;

  const results = await Promise.all(
    UPDATED_PRODUCT_KEYS.map(async (key) => {
      const data = await fetchProduct(getStripeProductID(key));
      return { key, data };
    }),
  );

  for (const { key, data } of results) {
    if (data !== null) {
      state.products[key] = data.product;
      state.prices = state.prices.concat(data.prices);
    }
  }

  state.done = true;
  state.loading = false;
}

export async function fetchProduct(stripeProductID: string) {
  const { data, error } = await getStripeProduct(stripeProductID);
  if (error) {
    await handleGenericErrors(error);
  } else if (data) {
    return data;
  }
  return null;
}

/** Ends the Stripe subscription trial now (first paid invoice). Refreshes user/org from `/users/info`. */
export async function completeStripeTrialEarly(): Promise<boolean> {
  const { data, error } = await earlyTrialCompletion();
  if (error) {
    await handleGenericErrors(error);
    return false;
  }
  if (!data) {
    noticesStore.error('Something went wrong, please try again later!');
    return false;
  }
  await userStore.refetchUserInfo();
  noticesStore.success('Your subscription is activating, please refresh after a minute or two.');
  return true;
}

export function getProductByID(stripeProductID: string) {
  for (const key of UPDATED_PRODUCT_KEYS) {
    const product = state.products[key];
    if (product && product.id === stripeProductID) {
      return product;
    }
  }
  return null;
}

export function getPriceByID(stripePriceID: string) {
  for (let i = 0; i < state.prices.length; i++) {
    const price = state.prices[i];
    if (price && price.id === stripePriceID) {
      return price;
    }
  }
  return null;
}

export function getDefaultPriceByProduct(
  product: StripeProduct | null,
): StripePrice | null {
  if (product === null) {
    return null;
  }
  for (let i = 0; i < state.prices.length; i++) {
    const price = state.prices[i];
    if (price.id === product.default_price) {
      return price;
    }
  }
  return null;
}

export function getPriceByProductAndMode(
  product?: string,
  mode: 'onetime' | 'month' | 'year' = 'month',
): StripePrice | null {
  if (typeof product !== 'string') {
    return null;
  }

  const productObj = getProductByID(product);
  if (productObj && productObj.default_price) {
    const defaultPrice = getPriceByID(productObj.default_price);
    if (defaultPrice) {
      const interval =
        (defaultPrice.recurring && defaultPrice.recurring.interval) ||
        'onetime';
      if (interval === mode) {
        return defaultPrice;
      }
    }
  }

  for (let i = 0; i < state.prices.length; i++) {
    const price = state.prices[i];
    const interval = (price.recurring && price.recurring.interval) || 'onetime';
    if (price.product === product && interval === mode) {
      return price;
    }
  }

  return null;
}

/** Yearly discount vs paying monthly for 12 months, for a given catalog product. */
export function getSavings(key: UpdatedProductKey): number | null {
  const product = state.products[key];
  const productId = product?.id;
  if (!productId) {
    return null;
  }
  const monthlyAsYear = getPriceByProductAndMode(productId, 'month');
  const yearlyPrice = getPriceByProductAndMode(productId, 'year');
  if (monthlyAsYear && yearlyPrice) {
    return Math.floor(
      100 *
      (1 -
        yearlyPrice.unit_amount /
        (12 * monthlyAsYear.unit_amount)),
    );
  }
  return null;
}

export function formatMoney(v: number) {
  return v / 100;
}

export function formatInterval(type: string) {
  switch (type) {
    case 'month':
      return 'mo';
    case 'year':
      return 'yr';
    default:
      return 'order';
  }
}

export function formatDigits(v: number, length: number) {
  let str = v + '';
  while (str.length < length) {
    str = `0${str}`;
  }
  return str;
}
